import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import SignatureBlock from '../SignatureBlock';
import ConventionDownload from '../ConventionDownload';
import ConventionEditor from '../ConventionEditor';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { Send, Eye, Edit, CheckCircle } from 'lucide-react';
import { formatCodeNAF, getCodeNAFLabel } from '../../utils/codeNaf';

export default function ModalsDetail({
  openDetailModal, setOpenDetailModal,
  setOpenEnvoiModal,
  selectedConvention,
  isEditing, setIsEditing,
  editFormData, setEditFormData,
  handleOpenEdit,
  handleSaveEdit,
  handleCancelEdit,
  getStatutBadge,
  getTypeIcon,
}) {
  return (
    <Dialog open={openDetailModal} onOpenChange={(open) => {
      setOpenDetailModal(open);
      if (!open) {
        setIsEditing(false);
        setEditFormData(null);
      }
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Edit className="h-5 w-5 text-blue-600" />
                  Modifier la convention
                </>
              ) : (
                <>
                  <Eye className="h-5 w-5 text-slate-600" />
                  Détails de la convention
                </>
              )}
            </DialogTitle>
            {selectedConvention && !isEditing && selectedConvention.statut === 'brouillon' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenEdit(selectedConvention)}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            )}
          </div>
        </DialogHeader>

        {selectedConvention && (
          <div className="space-y-4 py-4">
            {/* En-tête avec badge statut */}
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              {getTypeIcon(selectedConvention.type_convention)}
              <div className="flex-1">
                {isEditing ? (
                  <Input
                    value={editFormData?.titre || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, titre: e.target.value }))}
                    className="text-lg font-bold"
                    placeholder="Titre de la convention"
                  />
                ) : (
                  <h3 className="font-bold text-lg">{selectedConvention.titre}</h3>
                )}
                <p className="text-sm text-slate-600">{selectedConvention.numero_contrat}</p>
              </div>
              {getStatutBadge(selectedConvention.statut)}
            </div>

            {/* Mode édition */}
            {isEditing ? (
              <div className="space-y-6">
                {/* Éditeur de contenu amélioré */}
                <div>
                  <Label className="font-bold mb-2 block">Contenu de la convention *</Label>
                  <ConventionEditor
                    value={editFormData?.contenu_html || ''}
                    onChange={(html) => setEditFormData(prev => ({ ...prev, contenu_html: html }))}
                    type={selectedConvention.type_convention || 'cooper'}
                    height="350px"
                  />
                </div>

                {/* Options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold mb-2 block">Durée (mois)</Label>
                    <Input
                      type="number"
                      value={editFormData?.duree_mois || 12}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, duree_mois: parseInt(e.target.value) }))}
                      min={1}
                      max={120}
                    />
                  </div>
                  <div>
                    <Label className="font-bold mb-2 block">Montant mensuel (€)</Label>
                    <Input
                      type="number"
                      value={editFormData?.montant_mensuel || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, montant_mensuel: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Optionnel"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Conditions spécifiques */}
                <div>
                  <Label className="font-bold mb-2 block">Conditions spécifiques</Label>
                  <Textarea
                    value={editFormData?.conditions_specifiques || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, conditions_specifiques: e.target.value }))}
                    rows={4}
                    placeholder="Conditions particulières à cette convention..."
                  />
                </div>

                {/* Info partenaire externe si type "autre" */}
                {selectedConvention.type_convention === 'autre' && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <Label className="font-bold mb-3 block">Informations du partenaire</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Prénom</Label>
                        <Input
                          value={editFormData?.destinataire_autre?.prenom || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, prenom: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Nom</Label>
                        <Input
                          value={editFormData?.destinataire_autre?.nom || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, nom: e.target.value }
                          }))}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-sm">Email</Label>
                        <Input
                          type="email"
                          value={editFormData?.destinataire_autre?.email || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, email: e.target.value }
                          }))}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-sm">Entreprise</Label>
                        <Input
                          value={editFormData?.destinataire_autre?.entreprise || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, entreprise: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">SIRET</Label>
                        <Input
                          value={editFormData?.destinataire_autre?.siret || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, siret: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Code NAF</Label>
                        <Input
                          value={editFormData?.destinataire_autre?.code_naf || ''}
                          onChange={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, code_naf: e.target.value.toUpperCase() }
                          }))}
                          onBlur={(e) => setEditFormData(prev => ({
                            ...prev,
                            destinataire_autre: { ...prev.destinataire_autre, code_naf: formatCodeNAF(e.target.value) }
                          }))}
                          placeholder="Ex: 43.99C"
                          maxLength={6}
                          className="font-mono"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Mode visualisation */
              <>
                {/* Statut et Timeline */}
                <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-lg">Statut de la Convention</h4>
                      {getStatutBadge(selectedConvention.statut)}
                    </div>

                    <div className="space-y-3">
                      {/* Timeline des événements */}
                      <div className="relative pl-6 space-y-3">
                        {/* Ligne verticale */}
                        <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-blue-300"></div>

                        {/* Création */}
                        <div className="relative">
                          <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-blue-700 border-2 border-white"></div>
                          <div className="text-sm">
                            <p className="font-semibold text-blue-900">Créé le {new Date(selectedConvention.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}</p>
                            <p className="text-xs text-slate-600">par {selectedConvention.created_by || 'Admin'}</p>
                          </div>
                        </div>

                        {/* Envoi */}
                        {selectedConvention.date_envoi && (
                          <div className="relative">
                            <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-orange-600 border-2 border-white"></div>
                            <div className="text-sm">
                              <p className="font-semibold text-orange-900">Envoyé le {new Date(selectedConvention.date_envoi).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</p>
                              <p className="text-xs text-slate-600">En attente de signature</p>
                            </div>
                          </div>
                        )}

                        {/* Signature */}
                        {selectedConvention.date_signature && (
                          <div className="relative">
                            <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-green-700 border-2 border-white"></div>
                            <div className="text-sm">
                              <p className="font-semibold text-green-900">✍️ Signé le {new Date(selectedConvention.date_signature).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</p>
                              <p className="text-xs text-slate-600">
                                par {selectedConvention.signature_electronique || selectedConvention.destinataire_nom}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Dates actif */}
                        {selectedConvention.date_debut && (
                          <div className="relative">
                            <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-green-600 border-2 border-white"></div>
                            <div className="text-sm">
                              <p className="font-semibold text-green-900">Actif depuis le {new Date(selectedConvention.date_debut).toLocaleDateString('fr-FR')}</p>
                              {selectedConvention.date_fin && (
                                <p className="text-xs text-orange-600">
                                  ⏰ Expire le {new Date(selectedConvention.date_fin).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Suspension */}
                        {selectedConvention.date_suspendre && (
                          <div className="relative">
                            <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-red-600 border-2 border-white"></div>
                            <div className="text-sm">
                              <p className="font-semibold text-red-900">⏸️ Suspendu le {new Date(selectedConvention.date_suspendre).toLocaleDateString('fr-FR')}</p>
                              {selectedConvention.raison_suspendre && (
                                <p className="text-xs text-slate-600 italic">Raison: {selectedConvention.raison_suspendre}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Résiliation */}
                        {selectedConvention.date_resilier && (
                          <div className="relative">
                            <div className="absolute left-[-1.375rem] w-4 h-4 rounded-full bg-red-700 border-2 border-white"></div>
                            <div className="text-sm">
                              <p className="font-semibold text-red-900">🚫 Résilié le {new Date(selectedConvention.date_resilier).toLocaleDateString('fr-FR')}</p>
                              {selectedConvention.raison_resilier && (
                                <p className="text-xs text-slate-600 italic">Raison: {selectedConvention.raison_resilier}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Informations générales */}
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded">
                  <div>
                    <Label className="text-xs text-slate-600">Type</Label>
                    <p className="font-semibold">
                      {selectedConvention.type_convention === 'cooper' ? 'COOPER\'S' :
                       selectedConvention.type_convention === 'expert' ? 'EXPERT' : 'Autre Partenaire'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Durée</Label>
                    <p className="font-semibold">{selectedConvention.duree_mois} mois</p>
                  </div>
                  {selectedConvention.montant_mensuel && (
                    <div>
                      <Label className="text-xs text-slate-600">Montant mensuel</Label>
                      <p className="font-semibold">{selectedConvention.montant_mensuel} €</p>
                    </div>
                  )}
                  {selectedConvention.destinataire_nom && (
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-600">Destinataire</Label>
                      <p className="font-semibold">{selectedConvention.destinataire_nom}</p>
                    </div>
                  )}
                  {selectedConvention.type_convention === 'autre' && selectedConvention.destinataire_autre && (
                    <div className="col-span-2 bg-blue-50 p-3 rounded border border-blue-200">
                      <Label className="text-xs text-slate-600 mb-2 block">Informations Partenaire</Label>
                      <div className="space-y-1 text-sm">
                        <p><strong>Nom :</strong> {selectedConvention.destinataire_autre.prenom} {selectedConvention.destinataire_autre.nom}</p>
                        <p><strong>Email :</strong> {selectedConvention.destinataire_autre.email}</p>
                        {selectedConvention.destinataire_autre.entreprise && (
                          <p><strong>Entreprise :</strong> {selectedConvention.destinataire_autre.entreprise}</p>
                        )}
                        {selectedConvention.destinataire_autre.siret && (
                          <p><strong>SIRET :</strong> {selectedConvention.destinataire_autre.siret}</p>
                        )}
                        {selectedConvention.destinataire_autre.code_naf && (
                          <p>
                            <strong>Code NAF :</strong>{' '}
                            <span className="font-mono">{formatCodeNAF(selectedConvention.destinataire_autre.code_naf)}</span>
                            {getCodeNAFLabel(selectedConvention.destinataire_autre.code_naf) && (
                              <span className="text-slate-500"> — {getCodeNAFLabel(selectedConvention.destinataire_autre.code_naf)}</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contenu de la convention - AFFICHAGE COMPLET */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="font-bold">Contenu de la convention</Label>
                    <Badge className="bg-slate-600 text-white font-semibold">
                      Document complet
                    </Badge>
                  </div>
                  <div
                    className="border-2 rounded-lg p-6 bg-white overflow-y-auto prose prose-sm max-w-none"
                    style={{ maxHeight: 'none', minHeight: '300px' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedConvention.contenu_html) }}
                  />
                </div>

                {selectedConvention.conditions_specifiques && (
                  <div>
                    <Label className="font-bold mb-2 block">Conditions spécifiques</Label>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap bg-yellow-50 p-4 rounded border border-yellow-200">
                      {selectedConvention.conditions_specifiques}
                    </p>
                  </div>
                )}

                {/* Bloc de signature - Affiché uniquement après envoi */}
                {selectedConvention.statut !== 'brouillon' && (
                  <SignatureBlock
                    convention={selectedConvention}
                    isSignable={false}
                  />
                )}

                {/* Boutons téléchargement/consultation - Affiché pour conventions signées */}
                {(selectedConvention.statut === 'signe' || selectedConvention.statut === 'actif') && (
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Convention signée - Actions disponibles
                    </h4>
                    <ConventionDownload convention={selectedConvention} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                Annuler
              </Button>
              <Button onClick={handleSaveEdit} className="bg-green-600 hover:bg-green-700 text-white">
                <CheckCircle className="h-4 w-4 mr-2" />
                Sauvegarder les modifications
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpenDetailModal(false)}>
                Fermer
              </Button>
              {selectedConvention && selectedConvention.statut === 'brouillon' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleOpenEdit(selectedConvention)}
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => {
                      setOpenDetailModal(false);
                      setOpenEnvoiModal(true);
                    }}
                    className="bg-[#d4af37] hover:bg-[#c4a030] text-white"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
